import { isDefined } from '@sgme/fp';
import { HStack, Tooltip, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { TimeProgress } from '@/App/tiles/shared/TimeProgress.tsx';
import { useTargetAccumulatorSelector } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';

import '../../../../utils/rateComponentStyling.css';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton/index.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { targetAccumulatorApi, useCancelTargetAccumulatorRfsMutation } from '@/store/targetAccumulator/api/index.ts';
import {
  getTargetAccumulatorQuote,
  getTargetAccumulatorQuoteType,
  getTargetAccumulatorRfsId,
  isPremiumAmountNegative,
  selectTargetAccumulatorRfsStatus,
} from '@/store/targetAccumulator/selectors.ts';
import type { FxTargetAccumulator } from '@/store/targetAccumulator/state.ts';
import { requestTargetAccumulatorRfsThunk } from '@/store/targetAccumulator/thunk.ts';

export function TargetAccumulatorPriceButton({ tileId }: { tileId: string }) {
  return (
    <VStack fullWidth>
      <div className="py-2">
        <PriceButtons tileId={tileId} />
      </div>
    </VStack>
  );
}

function PriceButtons({ tileId }: { tileId: string }) {
  const rfsStatus = useTargetAccumulatorSelector((targetAccumulator) =>
    selectTargetAccumulatorRfsStatus(targetAccumulator, true),
  );

  const quoteType = useTargetAccumulatorSelector((targetAccumulator) =>
    getTargetAccumulatorQuoteType(targetAccumulator),
  );

  const {
    currentUser: { userType },
  } = useUserContext();

  const isInternalSales = userType === 'Internal Sales';

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable quoteType={quoteType} isInternalSales={isInternalSales} />;

    case 'STREAMING_EXECUTABLE':
      return <Progress tileId={tileId} />;

    case 'AWAITING_INDICATIVE':
      return <ShortcutExecutable tileId={tileId} />;

    case 'AWAITING_EXECUTABLE':
      return <AwaitingExecutable tileId={tileId} />;

    case 'PRICEABLE':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'ERROR':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'STREAMING_INDICATIVE':
      return <Priceable tileId={tileId} pricingType="Tradeable" forceTradeable={true} />;

    case 'BYPASSABLE_ERROR':
      return <Bypassable tileId={tileId} />;
    default:
      return <NotPriceable quoteType={quoteType} isInternalSales={isInternalSales} />;
  }
}

function AwaitingExecutable({ tileId }: { tileId: string }) {
  const [cancelTargetAccumulatorRfs] = useCancelTargetAccumulatorRfsMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator.properties.clientsWay);
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const rfsId = useTargetAccumulatorSelector(getTargetAccumulatorRfsId);
  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Cancel request for executable price',
      isDefined(rfsId) ? rfsId : 'unknown',
    );

    if (isDefined(rfsId)) {
      cancelTargetAccumulatorRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelTargetAccumulatorRfs, sgwtWebAnalytics]);

  return (
    <div className="progress" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <button
        type="button"
        className="btn btn-sm btn-flat-primary"
        data-e2e="cancel-rfs-button"
        onClick={handleCancelRequest}
      >
        <i className="icon">close</i>
      </button>
    </div>
  );
}

function Progress({ tileId }: { tileId: string }) {
  const [cancelTargetAccumulatorRfs] = useCancelTargetAccumulatorRfsMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const clientsWay = useTargetAccumulatorSelector((targetAccumulator) => {
    return targetAccumulator.properties.clientsWay;
  });

  const progressData = useTargetAccumulatorSelector(selectProgressData);
  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Cancel request for executable price',
      isDefined(progressData?.rfsId) ? progressData.rfsId : 'unknown',
    );

    if (isDefined(progressData?.rfsId)) {
      cancelTargetAccumulatorRfs({
        tileId,
        rfsId: progressData.rfsId,
      });
    }
  }, [progressData?.rfsId, tileId, cancelTargetAccumulatorRfs, sgwtWebAnalytics]);

  return (
    <TimeProgress
      totalTime={progressData?.totalTime}
      remainingTime={progressData?.remainingTime}
      onCancel={handleCancelRequest}
      data-e2e="rfs"
      clientsWay={clientsWay}
    />
  );
}

function Priceable({
  tileId,
  pricingType,
  forceTradeable,
}: {
  tileId: string;
  pricingType: 'Tradeable' | 'Indicative';
  forceTradeable: boolean;
}) {
  const dispatch = useAppDispatch();
  const rfsId = useTargetAccumulatorSelector(getTargetAccumulatorRfsId);
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator.properties.clientsWay);
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const skippedCreditCheck = useAppSelector((state) => {
    const rfsResponse = targetAccumulatorApi.endpoints.requestTargetAccumulatorRfs.select({
      requestId: rfsId,
      fixedCacheKey: rfsId,
    })(state);
    return rfsResponse.isSuccess && rfsResponse.data.skipCreditCheck;
  });

  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className={clsx('btn btn-sm btn-block', {
        'btn-sell': showColoredPrices && clientsWay === 'Sell',
        'btn-buy': showColoredPrices && clientsWay === 'Buy',
        'btn-info': !showColoredPrices,
        'btn-warning': skippedCreditCheck,
      })}
      onClick={() => {
        sgwtWebAnalytics?.trackEvent(
          'Target Accumulator',
          'Request price',
          `${pricingType} - ${forceTradeable ? 'force tradeable' : 'standard'}`,
        );

        dispatch(requestTargetAccumulatorRfsThunk({ tileId, forceTradeable, skipCreditCheck: skippedCreditCheck }));
      }}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function NotPriceable({
  quoteType,
  isInternalSales,
}: Readonly<{
  quoteType: 'Indicative' | 'Tradeable' | 'Withdraw';
  isInternalSales: boolean;
}>) {
  const premiumNegative = useTargetAccumulatorSelector(isPremiumAmountNegative);
  const disallowedMessage = isInternalSales ? 'PRIME_DISALLOWED.SALES' : 'PRIME_DISALLOWED.TRADERS';
  const message = premiumNegative ? disallowedMessage : quoteType;

  return (
    <button
      data-e2e={`price-button-${quoteType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className="btn btn-info btn-sm btn-block"
      disabled={true}
    >
      <FormattedMessage id={`request.${message}.price`} />
    </button>
  );
}

function Bypassable({ tileId }: { tileId: string }) {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const quoteType = useTargetAccumulatorSelector(getTargetAccumulatorQuoteType);
  const bypassCheck = true;
  const forceTradeable = quoteType === 'Indicative' || quoteType === 'Withdraw';

  function bypass() {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Bypass to executable price', tileId);
    dispatch(requestTargetAccumulatorRfsThunk({ tileId, forceTradeable: false, skipCreditCheck: true }));
  }
  function skipToExecutable() {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Skip to executable price', tileId);
    dispatch(requestTargetAccumulatorRfsThunk({ tileId, forceTradeable, skipCreditCheck: true }));
  }

  return (
    <HStack fullWidth>
      <button onClick={bypass} type="button" className="btn btn-warning btn-sm btn-block" data-e2e={'button-price'}>
        <FormattedMessage
          id={
            quoteType === 'Indicative'
              ? 'option.pricingButton.bypass.indicative.label'
              : 'option.pricingButton.bypass.executable.label'
          }
        />
      </button>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={skipToExecutable}
          data-e2e={'button-skip-to-executable-price'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </HStack>
  );
}

function ShortcutExecutable({ tileId }: { tileId: string }) {
  const dispatch = useAppDispatch();
  const rfsId = useTargetAccumulatorSelector(getTargetAccumulatorRfsId);
  const [cancelTargetAccumulatorRfs] = useCancelTargetAccumulatorRfsMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator.properties.clientsWay);
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Cancel request for executable price',
      isDefined(rfsId) ? rfsId : 'unknown',
    );

    if (isDefined(rfsId)) {
      cancelTargetAccumulatorRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelTargetAccumulatorRfs, sgwtWebAnalytics]);

  const bypassCheck = false;
  return (
    <div className="progress" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <SgButton
        type="button"
        purpose="discreet"
        size="sm"
        className="btn btn-sm btn-flat-primary flex-grow-0"
        onClick={handleCancelRequest}
      >
        <i className="icon icon-sm">close</i>
      </SgButton>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={() => {
            sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Skip to executable price', tileId);
            dispatch(requestTargetAccumulatorRfsThunk({ tileId, forceTradeable: true }));
          }}
          data-e2e={'skip-to-executable-price-button'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </div>
  );
}

function selectProgressData(targetAccumulator: FxTargetAccumulator) {
  const quote = getTargetAccumulatorQuote(targetAccumulator);
  const totalTime =
    targetAccumulator.properties.rfs?.status === 'PRICING'
      ? targetAccumulator.properties.rfs.initialRfsWindow
      : undefined;

  return {
    rfsId: getTargetAccumulatorRfsId(targetAccumulator),
    remainingTime: quote?.rfsWindow,
    totalTime,
  };
}
